import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { kollalogowhite, mobileheader, desktopheader } from "../../../assets";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const sections = ["Home", "AboutUs", "Layout",  "FloorPlans", "Location"  ,"Contact"];

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    sections.forEach((section, index) => {
      ScrollTrigger.create({
        trigger: `#${section}`,
        start: "top center",
        onEnter: () => setActiveTab(index),
        onEnterBack: () => setActiveTab(index),
      });
    });
  }, []);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    gsap.to(window, {
      duration: 1,
      scrollTo: { y: `#${sections[index]}`, offsetY: 70 },
      ease: "power2.out",
    });
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (drawerOpen) {
      gsap.fromTo(
        ".drawer-item",
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, stagger: 0.1, duration: 0.5, ease: "power2.out" }
      );
    }
  }, [drawerOpen]);

  return (
    <AppBar
      position="fixed"
      sx={{
        // backgroundImage: isMobile ? `url(${mobileheader})` : `url(${desktopheader})`,
        background: 'rgba(0,0,0,1)',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "10px 0",
        boxShadow: "0px -15px 60px rgba(216, 127, 44, 0.5)",
        borderRadius: "0",
        zIndex: 100000000,
        backdropFilter: "blur(15px)",
        transition: "all 0.3s",     
        height: isMobile ? "70px" : "74px",   
            }}
          >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height : isMobile ? "66px" : "74px" }}>
        <Box
          component="img"
          src={kollalogowhite}
          alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group"
          sx={{ cursor: "pointer", ml: { xs: 4, md: 5, lg: 6, xl: 6, xxl: 6 }, height : isMobile ? "30px" : "34px",  background: "linear-gradient(to right, rgba(0,0,0,0.95), rgba(0,0,0,0) 100%)", borderRadius: "0", margin: "5px 10px" }}
          onClick={() => navigate("/")}
        />

        {!isMobile ? (
          <Tabs
            value={activeTab}
            onChange={(_, index) => handleTabClick(index)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ "& .MuiTab-root": { fontWeight: activeTab === 0 ? "660" : "450" , fontSize: "16px", textTransform: "uppercase", margin: "0 7px", color: activeTab === 0 ? "primary.main" : "secondary.main" } }}
          >
            {sections.map((section) => (
              <Tab 
                key={section} 
                label={
                    <Typography variant="h6" fontWeight="500" color="primary" textAlign="center" sx={{ fontSize: "16px", textTransform: "uppercase" }}>
                    {section === "AboutUs" ? "About Us" : section === "FloorPlans" ? "Floor Plans" : section.charAt(0).toUpperCase() + section.slice(1)}
                    </Typography>
                } 
              />
            ))}
          </Tabs>
        ) : (
          // Mobile Drawer Button
          <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#fff" }}>
            <MenuIcon sx={{ fontSize: "32px" }} />
          </IconButton>
        )}
      </Toolbar>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "100%", background: "transparent", color: "#fff", p: 0, borderRadius: 0 } }}
      >

        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: "#000000", }}>
          <img src={kollalogowhite} alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" style={{ height: "25px", cursor: "pointer" }} onClick={() => handleTabClick(0)} />
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon sx={{ fontSize: "30px" }} />
          </IconButton>
        </Box>


        <List sx={{ padding: 0 }}>
          {sections.map((section, index) => (
            <>
              <ListItem
                key={section}
                onClick={() => handleTabClick(index)}
                className="drawer-item"
                sx={{
                  padding: "12px 24px",
                  transition: "all 0.3s ease-in-out",
                  borderBottom: "3px solid transparent",
                  borderImageSource:
                    activeTab === index
                      ? "linear-gradient(to right,rgb(204, 139, 90), #8b5e3b)"
                      : "linear-gradient(to right, #444, #666)",
                  borderImageSlice: 1,
                  backgroundColor: activeTab === index ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(15px)", opacity: 1,
                  "&:hover": {
                    backgroundColor: "#333333",
                    cursor: "pointer",
                    borderImageSource: "linear-gradient(to right,rgb(204, 139, 90), #8b5e3b)",
                  },
                  "& $ListItemText": { color: "#fff", fontWeight: "bold", fontSize: "16px" },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={activeTab === index ? "600" : "500"}
                  textAlign="center"
                  sx={{
                    fontSize: "16px",
                    textTransform: "uppercase",
                    color: activeTab === index ? "white" : "primary.main",
                  }}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </Typography>
              </ListItem>
            </>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Header;